import { useEffect, useMemo, useRef, useState } from 'react';

import ButtonWithBubble from '@components/Button/ButtonWithBubble';

import { SCORECARD_TESTS } from '../constants';
import styles from '../style.module.scss';
import {
  type ContactDetails,
  type ScorecardPayload,
  type ScorecardPdfApi,
  type ScoredRow,
  type Verdict,
} from '../types';
import { ContactFields, type ContactFieldErrors } from './ContactFields';
import { ScorecardGrid } from './ScorecardGrid';
import { ScorecardPattern } from './ScorecardPattern';

declare global {
  interface Window {
    InteractScorecardPdf?: ScorecardPdfApi;
  }
}

interface ScorecardAppProps {
  eyebrow: string;
  heading: string;
  subheading: string;
}

type Status = 'idle' | 'submitting' | 'success' | 'error';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const EMPTY_CONTACT: ContactDetails = { userName: '', email: '', platform: '' };

const PDF_LOAD_TIMEOUT_MS = 15000;

/**
 * Inject the self-hosted PDF bundle once, then poll for the global it exposes.
 * Polling (rather than waiting on a load event) avoids hanging when the script
 * tag already exists but fired its events before this call attached listeners.
 */
const loadPdfApi = (src: string): Promise<ScorecardPdfApi> =>
  new Promise((resolve, reject) => {
    if (window.InteractScorecardPdf) {
      resolve(window.InteractScorecardPdf);
      return;
    }

    const started = Date.now();
    const poll = window.setInterval(() => {
      if (window.InteractScorecardPdf) {
        window.clearInterval(poll);
        resolve(window.InteractScorecardPdf);
      } else if (Date.now() - started > PDF_LOAD_TIMEOUT_MS) {
        window.clearInterval(poll);
        reject(new Error('PDF module failed to load'));
      }
    }, 60);

    if (!document.querySelector('script[data-interact-scorecard-pdf]')) {
      const script = document.createElement('script');
      script.src = src;
      script.async = true;
      script.dataset.interactScorecardPdf = 'true';
      // Remove the failed tag so a later retry re-injects rather than waiting on it.
      script.addEventListener(
        'error',
        () => {
          window.clearInterval(poll);
          script.remove();
          reject(new Error('PDF module failed to load'));
        },
        { once: true },
      );
      document.body.appendChild(script);
    }
  });

export function ScorecardApp({ eyebrow, heading, subheading }: ScorecardAppProps) {
  const [verdicts, setVerdicts] = useState<Record<number, Verdict>>({});
  const [contact, setContact] = useState<ContactDetails>(EMPTY_CONTACT);
  const [fieldErrors, setFieldErrors] = useState<ContactFieldErrors>({});
  const [selectionError, setSelectionError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [status, setStatus] = useState<Status>('idle');
  const [message, setMessage] = useState('');
  const [lastPayload, setLastPayload] = useState<ScorecardPayload | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  const rows: ScoredRow[] = useMemo(
    () => SCORECARD_TESTS.map((t) => ({ ...t, verdict: verdicts[t.n] ?? null })),
    [verdicts],
  );

  const score = useMemo(() => {
    const essentials = rows.filter((r) => r.essential);
    return {
      passed: rows.filter((r) => r.verdict === 'pass').length,
      total: rows.length,
      essentialsPassed: essentials.filter((r) => r.verdict === 'pass').length,
      essentialsTotal: essentials.length,
    };
  }, [rows]);

  const setField = (field: keyof ContactDetails, value: string) => {
    setContact((c) => ({ ...c, [field]: value }));
    // Clear a field's error as soon as the user edits it.
    setFieldErrors((e) => (e[field] ? { ...e, [field]: undefined } : e));
  };

  useEffect(() => {
    // Skip the initial render so the page doesn't jump to the block on load.
    if (status === 'success') {
      rootRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [status]);

  const focusById = (id: string) =>
    window.setTimeout(() => document.getElementById(id)?.focus(), 0);

  const submit = async () => {
    const trimmed: ContactDetails = {
      userName: contact.userName.trim(),
      email: contact.email.trim(),
      platform: contact.platform.trim(),
    };

    const errors: ContactFieldErrors = {};
    if (!trimmed.userName) {
      errors.userName = 'Please enter your name.';
    }
    if (!trimmed.email) {
      errors.email = 'Please enter your email address.';
    } else if (!EMAIL_RE.test(trimmed.email)) {
      errors.email = 'Please enter a valid email address.';
    }
    if (!trimmed.platform) {
      errors.platform = 'Please enter the platform being scored.';
    }

    const unanswered = rows.filter((r) => r.verdict === null);
    const selectionMsg =
      unanswered.length > 0
        ? `Please score every test — ${unanswered.length} of ${rows.length} still to complete.`
        : null;

    setFieldErrors(errors);
    setSelectionError(selectionMsg);
    setFormError(null);

    if (errors.userName) {
      focusById('sc-user-name');
      return;
    }
    if (errors.email) {
      focusById('sc-email');
      return;
    }
    if (errors.platform) {
      focusById('sc-platform');
      return;
    }
    if (selectionMsg) {
      const first = unanswered[0];
      window.setTimeout(
        () =>
          document
            .querySelector<HTMLInputElement>(`input[name="ai-scorecard-row-${first.n}"]`)
            ?.focus(),
        0,
      );
      return;
    }

    const config = window.InteractScorecardData;
    if (!config) {
      setStatus('error');
      setFormError('Something went wrong. Please refresh and try again.');
      return;
    }

    // One state object drives both the server record and the PDF, so they can't drift.
    const payload: ScorecardPayload = { contact: trimmed, rows, score };

    setStatus('submitting');
    setMessage('');

    let json: { success?: boolean; message?: string } | null;
    try {
      // No X-WP-Nonce: sending one makes WP core run its cookie-nonce check,
      // which 403s ("Cookie check failed") on cached pages / in Safari. The
      // endpoint is guarded by a same-origin check + rate limit + ZeroBounce.
      const res = await fetch(config.restUrl, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      json = await res.json().catch(() => null);
      if (!res.ok || !json?.success) {
        setStatus('error');
        setFormError(json?.message ?? 'Something went wrong. Please try again.');
        return;
      }
    } catch {
      setStatus('error');
      setFormError('Network error. Please try again.');
      return;
    }

    setLastPayload(payload);
    setStatus('success');
    setMessage('Download your completed scorecard below.');
  };

  // Download builds the PDF on a direct click — a real user gesture, so the
  // browser (Safari especially) never blocks it the way a post-await save can.
  const downloadPdf = async () => {
    const config = window.InteractScorecardData;
    if (!config || !lastPayload) {
      return;
    }
    setDownloadError(null);
    try {
      const pdf = await loadPdfApi(config.pdfScriptUrl);
      await pdf.generate(lastPayload, { fontUrl: config.fontUrl, logoUrl: config.logoUrl });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[ai-scorecard] PDF download failed:', err);
      setDownloadError('Sorry, the download failed. Please try again.');
    }
  };

  return (
    <div className={styles.scorecard} ref={rootRef}>
      <ScorecardPattern />

      <header className={styles.masthead}>
        {eyebrow ? <p className={styles.eyebrow}>{eyebrow}</p> : null}
        <h1 className={styles.heading}>{heading}</h1>
        {subheading ? <p className={styles.subheading}>{subheading}</p> : null}
      </header>

      {status === 'success' ? (
        <div className={styles.card}>
          <div className={styles.success}>
            <span className={styles.successIcon} aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none">
                <path
                  d="M5 12.5l4.5 4.5L19 6.5"
                  stroke="currentColor"
                  strokeWidth="2.4"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
            <p className={styles.intro}>{message}</p>
            <ButtonWithBubble
              variant="primary"
              className={styles.submitBubble}
              onClick={downloadPdf}
            >
              Download my scorecard
            </ButtonWithBubble>
            {downloadError ? <p className={styles.error}>{downloadError}</p> : null}
          </div>
        </div>
      ) : (
        <div className={`${styles.card} ${styles.gridCard}`}>
          <div className={styles.contact}>
            <ContactFields values={contact} errors={fieldErrors} onChange={setField} />
          </div>

          <ScorecardGrid
            rows={rows}
            onSelect={(n, verdict) => {
              setVerdicts((v) => ({ ...v, [n]: verdict }));
              setSelectionError(null);
            }}
          />

          <div className={styles.actions}>
            <ButtonWithBubble
              variant="primary"
              className={styles.submitBubble}
              onClick={submit}
              disabled={status === 'submitting'}
            >
              {status === 'submitting' ? 'Sending…' : 'Get my scorecard'}
            </ButtonWithBubble>
            {selectionError ? (
              <p className={styles.error} role="alert">
                {selectionError}
              </p>
            ) : null}
            {formError ? (
              <p className={styles.error} role="alert">
                {formError}
              </p>
            ) : null}
            <p className={styles.privacy}>
              Interact uses your details to send your scorecard and contact you about our services.
              You can unsubscribe at any time. See our{' '}
              <a href="/terms/privacy-policy/" target="_blank" rel="noopener noreferrer">
                Privacy Policy
              </a>
              .
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
